import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { CrosshairIcon, NavigationIcon, XIcon } from 'lucide-react'
import { captureGeolocation } from '../../lib/geolocation'
import { googleMapsDirectionsUrl, type LatLng } from '../../lib/routeOptimizer'
import { mobile } from '../mobile'

export type JobsDayMapJob = {
  id: string
  job_number: string
  title: string
  property?: {
    name?: string | null
    address?: string | null
    latitude?: number | null
    longitude?: number | null
  } | null
}

export function jobLatLng(
  job: JobsDayMapJob
): { latitude: number; longitude: number } | null {
  const lat = Number(job.property?.latitude)
  const lng = Number(job.property?.longitude)
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
  return { latitude: lat, longitude: lng }
}

interface JobsDayMapProps {
  jobs: JobsDayMapJob[]
  onClose: () => void
  onSelectJob?: (jobId: string) => void
}

type GMaps = {
  Map: new (el: HTMLElement, opts: Record<string, unknown>) => {
    fitBounds: (b: unknown, pad?: number) => void
    panTo: (ll: { lat: number; lng: number }) => void
    setZoom: (z: number) => void
    getZoom: () => number | undefined
  }
  Marker: new (opts: Record<string, unknown>) => { setMap: (m: unknown) => void; setPosition: (p: unknown) => void }
  InfoWindow: new (opts: Record<string, unknown>) => {
    setContent: (c: string) => void
    open: (map: unknown, anchor?: unknown) => void
  }
  LatLngBounds: new () => { extend: (ll: { lat: number; lng: number }) => void; isEmpty?: () => boolean }
  Size: new (w: number, h: number) => unknown
  Point: new (x: number, y: number) => unknown
  ControlPosition: { RIGHT_BOTTOM: unknown; RIGHT_TOP: unknown }
  event: { addListener: (t: unknown, n: string, fn: () => void) => void }
}

let mapsLoadPromise: Promise<GMaps> | null = null

async function resolveMapsApiKey(): Promise<string | null> {
  const envKey = process.env.REACT_APP_GOOGLE_PLACES_API_KEY
  if (envKey) return envKey
  try {
    const res = await fetch('/api/config/client')
    if (res.ok) {
      const data = await res.json()
      return data.googlePlacesApiKey || null
    }
  } catch {
    /* ignore */
  }
  return null
}

function loadGoogleMaps(): Promise<GMaps> {
  const existing = (window as unknown as { google?: { maps?: GMaps } }).google?.maps
  if (existing?.Map) return Promise.resolve(existing)
  if (mapsLoadPromise) return mapsLoadPromise

  mapsLoadPromise = (async () => {
    const key = await resolveMapsApiKey()
    if (!key) throw new Error('Google Maps API key not configured')

    const already = (window as unknown as { google?: { maps?: GMaps } }).google?.maps
    if (already?.Map) return already

    await new Promise<void>((resolve, reject) => {
      const ready = () => {
        const maps = (window as unknown as { google?: { maps?: GMaps } }).google?.maps
        if (maps?.Map) resolve()
        else reject(new Error('Google Maps failed to initialize'))
      }
      const tag = document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]')
      if (tag) {
        if ((window as unknown as { google?: { maps?: GMaps } }).google?.maps?.Map) {
          resolve()
          return
        }
        tag.addEventListener('load', () => ready())
        tag.addEventListener('error', () => reject(new Error('Failed to load Google Maps')))
        return
      }
      const script = document.createElement('script')
      script.src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=places`
      script.async = true
      script.defer = true
      script.onload = () => ready()
      script.onerror = () => reject(new Error('Failed to load Google Maps'))
      document.head.appendChild(script)
    })

    return (window as unknown as { google: { maps: GMaps } }).google.maps
  })().catch((err) => {
    mapsLoadPromise = null
    throw err
  })

  return mapsLoadPromise
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function numberedPinIcon(index: number): string {
  const n = String(index)
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="36" height="44" viewBox="0 0 36 44">
    <path d="M18 0C8.06 0 0 8.06 0 18c0 12.5 18 26 18 26s18-13.5 18-26C36 8.06 27.94 0 18 0z" fill="#2563eb"/>
    <circle cx="18" cy="17" r="11" fill="#1d4ed8"/>
    <text x="18" y="21.5" text-anchor="middle" fill="#fff" font-size="12" font-weight="700" font-family="Arial,sans-serif">${n}</text>
  </svg>`
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`
}

/** Full-screen Google Map of today's job pins. Close returns to the Home list. */
export function JobsDayMap({ jobs, onClose, onSelectJob }: JobsDayMapProps) {
  const mapElRef = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<InstanceType<GMaps['Map']> | null>(null)
  const userMarkerRef = useRef<InstanceType<GMaps['Marker']> | null>(null)
  const gmapsRef = useRef<GMaps | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [mapsReady, setMapsReady] = useState(false)
  const [locating, setLocating] = useState(false)
  const [openingMaps, setOpeningMaps] = useState(false)
  const onSelectJobRef = useRef(onSelectJob)
  onSelectJobRef.current = onSelectJob

  const withCoords = useMemo(
    () => jobs.filter((job) => jobLatLng(job)),
    [jobs]
  )
  const pinJobs = useMemo(
    () =>
      withCoords.map((job, pinIndex) => ({
        job,
        coords: jobLatLng(job)!,
        pinNumber: pinIndex + 1,
      })),
    [withCoords]
  )

  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    loadGoogleMaps()
      .then((gmaps) => {
        if (cancelled) return
        gmapsRef.current = gmaps
        setMapsReady(true)
      })
      .catch((err: Error) => {
        if (cancelled) return
        setLoadError(err.message || 'Could not load Google Maps')
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    const gmaps = gmapsRef.current
    const el = mapElRef.current
    if (!mapsReady || !gmaps || !el) return

    const map = new gmaps.Map(el, {
      clickableIcons: false,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      zoomControl: true,
      zoomControlOptions: { position: gmaps.ControlPosition.RIGHT_BOTTOM },
      gestureHandling: 'greedy',
      center: { lat: 40.7128, lng: -74.006 },
      zoom: 11,
    })
    mapRef.current = map

    const bounds = new gmaps.LatLngBounds()
    const info = new gmaps.InfoWindow({})

    pinJobs.forEach((row) => {
        const pos = { lat: row.coords.latitude, lng: row.coords.longitude }
        bounds.extend(pos)
        const marker = new gmaps.Marker({
          map,
          position: pos,
          title: row.job.title,
          icon: {
            url: numberedPinIcon(row.pinNumber),
            scaledSize: new gmaps.Size(36, 44),
            anchor: new gmaps.Point(18, 44),
          },
          zIndex: 100 + row.pinNumber,
        })
        gmaps.event.addListener(marker, 'click', () => {
          const select = onSelectJobRef.current
          const addr = escapeHtml(row.job.property?.name || row.job.property?.address || 'Job site')
          const viewLink = select
            ? `<button type="button" id="jobs-map-view-${row.job.id}" style="margin-top:6px;color:#2563eb;font-weight:600;background:none;border:0;padding:0;cursor:pointer">View in list</button>`
            : ''
          info.setContent(
            `<div style="font:13px/1.35 system-ui,sans-serif;max-width:220px">
            <div style="font-weight:700;color:#111">${row.pinNumber}. ${escapeHtml(row.job.title)}</div>
            <div style="color:#6b7280;margin-top:2px">#${escapeHtml(row.job.job_number)} · ${addr}</div>
            ${viewLink}
          </div>`
          )
          info.open(map, marker)
          if (select) {
            window.setTimeout(() => {
              document
                .getElementById(`jobs-map-view-${row.job.id}`)
                ?.addEventListener('click', () => select(row.job.id))
            }, 0)
          }
        })
      })

      if (pinJobs.length === 1) {
        map.panTo({
          lat: pinJobs[0].coords.latitude,
          lng: pinJobs[0].coords.longitude,
        })
      map.setZoom(15)
    } else if (pinJobs.length > 1) {
      map.fitBounds(bounds, 64)
    }

    return () => {
      mapRef.current = null
      userMarkerRef.current = null
    }
  }, [mapsReady, pinJobs])

  const showUserLocation = useCallback((pos: LatLng) => {
    const gmaps = gmapsRef.current
    const map = mapRef.current
    if (!gmaps || !map) return
    const ll = { lat: pos.latitude, lng: pos.longitude }
    if (!userMarkerRef.current) {
      userMarkerRef.current = new gmaps.Marker({
        map,
        position: ll,
        title: 'Your location',
        zIndex: 1000,
        icon: {
          url:
            'data:image/svg+xml;charset=UTF-8,' +
            encodeURIComponent(
              `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22">
                <circle cx="11" cy="11" r="10" fill="#fff"/>
                <circle cx="11" cy="11" r="7" fill="#2563eb"/>
              </svg>`
            ),
          scaledSize: new gmaps.Size(22, 22),
          anchor: new gmaps.Point(11, 11),
        },
      })
    } else {
      userMarkerRef.current.setPosition(ll)
    }
    map.panTo(ll)
    const z = map.getZoom()
    if (z == null || z < 14) map.setZoom(15)
  }, [])

  const handleLocateMe = async () => {
    setLocating(true)
    try {
      const geo = await captureGeolocation()
      if (!geo.position) {
        alert('Could not get your GPS location. Enable location access and try again.')
        return
      }
      showUserLocation({
        latitude: geo.position.latitude,
        longitude: geo.position.longitude,
      })
    } finally {
      setLocating(false)
    }
  }

  const handleOpenGoogleMaps = async () => {
    const stops = pinJobs.map((row) => row.coords)
    if (stops.length === 0) {
      alert('None of these jobs have GPS coordinates to open in Google Maps.')
      return
    }
    setOpeningMaps(true)
    try {
      const geo = await captureGeolocation()
      const origin = geo.position
        ? { latitude: geo.position.latitude, longitude: geo.position.longitude }
        : null
      if (!origin && stops.length > 1) {
        alert('GPS unavailable — opening the route from the first job instead.')
      }
      const url = googleMapsDirectionsUrl(stops, origin)
      if (!url) return
      window.open(url, '_blank', 'noopener,noreferrer')
    } finally {
      setOpeningMaps(false)
    }
  }

  const overlay = (
    <div className="fixed inset-0 z-[70] flex flex-col bg-white">
      <header className="relative flex items-center justify-center h-12 border-b border-gray-200 flex-shrink-0 bg-white">
        <button
          type="button"
          onClick={onClose}
          className="absolute left-1 inline-flex items-center justify-center min-h-[44px] min-w-[44px] rounded-xl text-gray-600 active:bg-gray-100"
          aria-label="Close map"
        >
          <XIcon className="w-5 h-5" />
        </button>
        <h1 className="text-base font-semibold text-gray-900">Map</h1>
      </header>

      <div className="relative flex-1 min-h-0 bg-slate-100">
        <div ref={mapElRef} className="absolute inset-0" />

        {loadError && (
          <div className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center bg-slate-50">
            <p className="text-sm font-medium text-gray-800">Could not load the map</p>
            <p className="text-sm text-gray-500 mt-1 max-w-xs">{loadError}</p>
          </div>
        )}

        {!loadError && pinJobs.length === 0 && mapsReady && (
          <div className="absolute inset-0 flex items-center justify-center px-6 text-center pointer-events-none">
            <p className="text-sm text-gray-600 bg-white/90 rounded-xl px-4 py-3 shadow-sm">
              No GPS coordinates on these jobs.
            </p>
          </div>
        )}

        <button
          type="button"
          onClick={() => void handleLocateMe()}
          disabled={locating || !mapsReady}
          className="absolute top-3 right-3 z-10 inline-flex h-11 w-11 items-center justify-center rounded-full bg-white text-gray-700 shadow-md border border-gray-200 active:bg-gray-50 disabled:opacity-50"
          aria-label="Locate me"
          title="Locate me"
        >
          <CrosshairIcon className={`w-5 h-5 ${locating ? 'animate-pulse' : ''}`} />
        </button>
      </div>

      <div
        className="flex-shrink-0 border-t border-gray-200 bg-white px-3 py-2.5"
        style={{ paddingBottom: 'max(0.625rem, env(safe-area-inset-bottom))' }}
      >
        <button
          type="button"
          onClick={() => void handleOpenGoogleMaps()}
          disabled={openingMaps || pinJobs.length === 0}
          className={`${mobile.primaryBtn} w-full disabled:opacity-50`}
        >
          <NavigationIcon className="w-4 h-4" />
          {openingMaps ? 'Opening…' : 'Open in Google Maps'}
        </button>
      </div>
    </div>
  )

  return createPortal(overlay, document.body)
}
